import { useEffect, useState } from "react"
import { ServicesIcon, PartsIcon, MarketplaceIcon, VehicleIcon, DiscoverIcon, PowerIcon, MechanicIcon, GlobeIcon, CameraIcon, UploadShareIcon, WarningIcon, ClockIcon, DocumentIcon, AnalyticsIcon } from "../../lib/cccIcons"
import { supabase } from "../../lib/supabase"
import { pushNotify } from "../../lib/pushNotify"
import { sanitizeName, sanitizePhone, sanitizeFreeText } from "../../lib/sanitize"
import { validatePassword } from "../../lib/passwordValidation"
import { getCurrentPosition } from "../../lib/geolocation"
import PhotoManager from "../shared/PhotoManager"
import { useAuth } from "../../contexts/AuthContext"
import { useLanguage } from "../../contexts/LanguageContext"
import useIsMobile from "../../lib/useIsMobile"
import { exportUserData, downloadJSON, downloadCSV, downloadPDF } from "../../lib/dataExport"
import toast from "react-hot-toast"

const PROVIDER_TYPES = [
  { key:"garage", label:"Garage/Mechanic", icon:"services" },
  { key:"parts_dealer", label:"Parts Dealer", icon:"parts" },
  { key:"accessories_shop", label:"Accessories Shop", icon:"marketplace" },
  { key:"tyre_shop", label:"Tyre Shop", icon:"vehicle" },
  { key:"auto_electrician", label:"Auto Electrician", icon:"power" },
  { key:"car_wash", label:"Car Wash", icon:"discover" },
  { key:"panel_beater", label:"Panel Beater", icon:"mechanic" },
  { key:"auto_glass", label:"Auto Glass", icon:"globe" },
]

export default function ProviderProfile() {
  const { profile, updateProfile, user } = useAuth()
  const { t } = useLanguage()
  const isMobile = useIsMobile()
  const [form, setForm] = useState({ first_name:"", last_name:"", business_name:"", city:"", go_service_radius_km:"15" })
  const [sensitive, setSensitive] = useState({ phone:"", email:"", mpesa_number:"", till_number:"", paybill_number:"", paybill_account:"", pochi_number:"", preferred_payment_method:"mpesa" })
  const [location, setLocation] = useState({ latitude:null, longitude:null, address:"" })
  const [saving, setSaving] = useState(false)
  const [locating, setLocating] = useState(false)
  const [tab, setTab] = useState("business")
  const [exporting, setExporting] = useState(false)
  const [exportData, setExportData] = useState(null)

  useEffect(() => {
    if (profile) {
      setForm({ first_name:profile?.first_name||"", last_name:profile?.last_name||"", carries_parts_for_go:profile?.carries_parts_for_go||false, business_name:profile?.business_name||"", city:profile?.city||"", go_service_radius_km:profile?.go_service_radius_km?String(profile.go_service_radius_km):"15" })
      setLocation({ latitude:profile?.latitude||null, longitude:profile?.longitude||null, address:"" })
    }
    if (user) loadSensitive()
  }, [profile, user])

  async function loadSensitive() {
    const { data } = await supabase.from("profile_sensitive").select("phone,email,mpesa_number,till_number,paybill_number,paybill_account,pochi_number,preferred_payment_method").eq("id", user.id).single()
    if (data) setSensitive({ phone:data.phone||"", email:data.email||"", mpesa_number:data.mpesa_number||"", till_number:data.till_number||"", paybill_number:data.paybill_number||"", paybill_account:data.paybill_account||"", pochi_number:data.pochi_number||"", preferred_payment_method:data.preferred_payment_method||"mpesa" })
  }

  async function saveProfile(e) {
    e.preventDefault()
    setSaving(true)
    try { await updateProfile({...form, carries_parts_for_go: form.carries_parts_for_go, go_service_radius_km: form.go_service_radius_km?Number(form.go_service_radius_km):15}); toast.success(t("saveChanges")) }
    catch(err) { toast.error(err.message) }
    finally { setSaving(false) }
  }

  async function saveContact(e) {
    e.preventDefault()
    setSaving(true)
    try {
      await supabase.from("profile_sensitive").update({ 
        phone:sensitive.phone, 
        mpesa_number:sensitive.mpesa_number,
        till_number:sensitive.till_number||null,
        paybill_number:sensitive.paybill_number||null,
        paybill_account:sensitive.paybill_account||null,
        pochi_number:sensitive.pochi_number||null,
        preferred_payment_method:sensitive.preferred_payment_method||"mpesa"
      }).eq("id", user.id)
      toast.success(t("saveChanges"))
    } catch(err) { toast.error(err.message) }
    finally { setSaving(false) }
  }

  async function saveLocation(e) {
    e.preventDefault()
    setSaving(true)
    try {
      await updateProfile({ latitude:parseFloat(location.latitude)||null, longitude:parseFloat(location.longitude)||null, city:form.city, address:location.address||null })
      toast.success("Location saved")
    } catch(err) { toast.error(err.message) }
    finally { setSaving(false) }
  }

  async function detectLocation() {
    setLocating(true)
    getCurrentPosition().then(async pos => {
        const lat = pos.latitude
        const lng = pos.longitude
        setLocation(l=>({...l, latitude:lat, longitude:lng}))
        try {
          const res = await fetch(`https://nominatim.openstreetmap.org/reverse?lat=${lat}&lon=${lng}&format=json`)
          const data = await res.json()
          const city = data.address?.city || data.address?.town || data.address?.suburb || ""
          setLocation(l=>({...l, address:data.display_name||""}))
          if (city) setForm(f=>({...f, city}))
        } catch { /* location parse failed - non-critical */ }
        setLocating(false)
      }).catch(err => { console.warn('Location error:', err.message); toast.error('Could not detect location: ' + err.message) }).finally(() => setLocating(false))
  }

  async function changePassword(e) {
    e.preventDefault()
    const pw = e.target.password.value
    const confirm = e.target.confirm.value
    if (pw !== confirm) return toast.error("Passwords do not match")
    const pwError = validatePassword(pw); if (pwError) return toast.error(pwError)
    const { error } = await supabase.auth.updateUser({ password: pw })
    if (error) return toast.error(error.message)
    toast.success("Password changed")
    e.target.reset()
  }

  async function loadExportData() {
    setExporting(true)
    try { const data = await exportUserData(user.id); setExportData(data) }
    catch(err) { toast.error(err.message) }
    finally { setExporting(false) }
  }

  const initials = `${profile?.first_name?.[0]||""}${profile?.last_name?.[0]||""}`.toUpperCase()
  const inp = { width:"100%", background:"#ffffff", border:"1px solid #e5e5e5", borderRadius:8, padding:"11px 12px", color:"#000000", fontSize:13, outline:"none", fontFamily:"'DM Sans',sans-serif", marginBottom:12 }
  const lbl = { fontSize:11, color:"#666", textTransform:"uppercase", letterSpacing:"0.05em", display:"block", marginBottom:4 }

  const TABS = [
    { k:"business", l:t("businessInfo") },
    { k:"contact", l:t("contactDetails") },
    { k:"location", l:"Location" },
    { k:"security", l:t("security") },
    { k:"data", l:"My Data" },
  ]

  return (
    <div style={{ maxWidth:isMobile?"100%":520 }}>
      {profile?.verification_status==="pending"&&!profile?.is_verified&&(
        <div style={{ background:"#fff8f0", border:"1px solid #e6821e40", borderRadius:10, padding:"0.75rem 1rem", marginBottom:"1rem", fontSize:12, color:"#e6821e" }}>
          <ClockIcon size={14} color="#e6821e"/> Your account is pending verification. Complete your profile and our team will review it soon.
        </div>
      )}
      {profile?.verification_status==="rejected"&&(
        <div style={{ background:"#fff5f5", border:"1px solid #e24b4a40", borderRadius:10, padding:"0.75rem 1rem", marginBottom:"1rem", fontSize:12, color:"#e24b4a" }}>
          ✗ Verification not approved: {profile?.verification_notes||"Please contact support."}
        </div>
      )}
      {profile?.is_verified&&(
        <div style={{ background:"#f0fdf4", border:"1px solid #1d9e7540", borderRadius:10, padding:"0.75rem 1rem", marginBottom:"1rem", fontSize:12, color:"#1d9e75" }}>
          ✓ Your account is verified and visible to customers!
        </div>
      )}
      <div style={{ background:"#fff8f0", border:"1px solid #e6821e40", borderRadius:10, padding:"0.75rem 1rem", marginBottom:"1rem" }}>
        <div style={{ fontSize:12, fontWeight:700, color:"#e6821e", marginBottom:4, display:"flex", alignItems:"center", gap:4 }}><UploadShareIcon size={12} color="#e6821e"/> Your public storefront</div>
        <div style={{ fontSize:11, color:"#666", marginBottom:8 }}>Share this link so customers can view your services and book directly.</div>
        <div style={{ display:"flex", gap:6, alignItems:"center" }}>
          <input readOnly value={`https://carcareconnect.care/provider/${user.id}`} onClick={e=>e.target.select()}
            style={{ flex:1, background:"#ffffff", border:"1px solid #e6821e40", borderRadius:7, padding:"6px 10px", fontSize:11, color:"#555", outline:"none" }}/>
          <button onClick={()=>{ navigator.clipboard.writeText(`https://carcareconnect.care/provider/${user.id}`); toast.success("Link copied!") }} 
            style={{ background:"#e6821e", border:"none", borderRadius:7, color:"#fff", fontSize:11, fontWeight:700, padding:"7px 14px", cursor:"pointer", flexShrink:0 }}>
            Copy
          </button>
        </div>
      </div>
      <div style={{ display:"flex", alignItems:"center", gap:16, marginBottom:"1.5rem" }}>
        <div style={{ width:60, height:60, borderRadius:14, background:"#eff6ff", border:"2px solid #378add40", display:"flex", alignItems:"center", justifyContent:"center", fontFamily:"Syne", fontSize:22, fontWeight:800, color:"#378add" }}>
          {initials}
        </div>
        <div>
          <div style={{ fontFamily:"Syne", fontSize:17, fontWeight:800, color:"#000000" }}>{profile?.business_name||`${profile?.first_name} ${profile?.last_name}`}</div>
          <div style={{ fontSize:12, color:"#777777", marginTop:2 }}>
            Provider · {profile?.city||"Location not set"}
            {profile?.provider_type&&<span style={{ marginLeft:6, fontSize:10, color:"#378add", background:"#eff6ff", padding:"1px 7px", borderRadius:10 }}>
            {PROVIDER_TYPES.find(p=>p.key===profile?.provider_type)?.icon} {profile?.provider_type?.replace(/_/g," ")}
            </span>}
          </div>
        </div>
      </div>

      <div style={{ display:"flex", gap:6, marginBottom:"1.5rem", flexWrap:"wrap" }}>
        {TABS.map(tb=>(
          <button key={tb.k} onClick={()=>{ setTab(tb.k); if(tb.k==="data"&&!exportData) loadExportData() }}
            style={{ padding:"8px 14px", borderRadius:8, border:"none", fontSize:12, cursor:"pointer", background:tab===tb.k?"#378add":"#555555", color:tab===tb.k?"#fff":"#ccc", fontFamily:"'DM Sans',sans-serif", fontWeight:tab===tb.k?700:400 }}>
            {tb.l}
          </button>
        ))}
      </div>

      {tab==="business"&&(
        <form onSubmit={saveProfile}>
          <div style={{ background:"#ffffff", border:"1px solid #eeeeee", borderRadius:12, padding:"1.25rem" }}>
            <div style={{ fontFamily:"Syne", fontSize:14, fontWeight:700, marginBottom:"1rem", color:"#000000" }}>{t("businessInfo")}</div>
            <label style={lbl}>Business name</label>
            <input style={inp} placeholder="Your business name" value={form.business_name} onChange={e=>setForm(f=>({...f,business_name:e.target.value}))}/>
            <div style={{ display:"grid", gridTemplateColumns:isMobile?"1fr":"1fr 1fr", gap:10 }}>
              <div><label style={lbl}>{t("firstName")}</label><input style={inp} value={form.first_name} onChange={e=>setForm(f=>({...f,first_name:e.target.value}))} required/></div>
              <div><label style={lbl}>{t("lastName")}</label><input style={inp} value={form.last_name} onChange={e=>setForm(f=>({...f,last_name:e.target.value}))} required/></div>
            </div>
            <label style={lbl}>{t("city")}</label>
            <input style={inp} placeholder="e.g. Nairobi" value={form.city} onChange={e=>setForm(f=>({...f,city:e.target.value}))}/>
            {["garage","garage_premium","auto_electrician","mobile_mechanic"].includes(profile?.provider_type)&&(
              <div style={{ marginBottom:12 }}>
                <label style={lbl}>🚨 GO Service radius (km)</label>
                <div style={{ fontSize:11, color:"#888", marginBottom:6 }}>Maximum distance you&apos;re willing to travel for emergency roadside assistance</div>
                <input type="number" min="1" max="100" style={inp} placeholder="15" value={form.go_service_radius_km} onChange={e=>setForm(f=>({...f,go_service_radius_km:e.target.value}))}/>
              <div style={{ marginBottom:12 }}>
                <label style={lbl}>🔧 Carries parts for GO Service</label>
                <div style={{ fontSize:11, color:"#888", marginBottom:6 }}>Let customers know your mechanics carry spare parts for emergency jobs</div>
                <div style={{ display:"flex", alignItems:"center", gap:10 }}>
                  <input type="checkbox" checked={form.carries_parts_for_go||false} onChange={e=>setForm(f=>({...f,carries_parts_for_go:e.target.checked}))} style={{ width:18, height:18, cursor:"pointer" }}/>
                  <span style={{ fontSize:12, color:"#555" }}>Yes, our mechanics carry common spare parts</span>
                </div>
              </div>
              </div>
            )}
            {["garage","garage_premium","auto_electrician","mobile_mechanic"].includes(profile?.provider_type)&&(
              <div style={{ marginBottom:12 }}>
                <label style={lbl}>🚨 GO Service radius (km)</label>
                <div style={{ fontSize:11, color:"#888", marginBottom:6 }}>Maximum distance you&apos;re willing to travel for emergency roadside assistance</div>
                <input type="number" min="1" max="100" style={inp} placeholder="15" value={form.go_service_radius_km} onChange={e=>setForm(f=>({...f,go_service_radius_km:e.target.value}))}/>
              </div>
            )}
            <label style={lbl}>Business type</label>
            <select value={form.provider_type||""} onChange={e=>setForm(f=>({...f,provider_type:e.target.value}))}
              style={{ width:"100%", background:"#f8f8f8", border:"1px solid #e5e5e5", borderRadius:8, padding:"11px 12px", color:"#000", fontSize:13, outline:"none", fontFamily:"DM Sans,sans-serif", marginBottom:12, cursor:"pointer" }}>
              <option value="">Select business type...</option>
              {PROVIDER_TYPES.map(pt=>(
                <option key={pt.key} value={pt.key}>{pt.icon} {pt.label}</option>
              ))}
            </select>
            <button type="submit" disabled={saving}
              style={{ background:saving?"#555555":"#378add", border:"none", borderRadius:9, color:"#fff", fontFamily:"Syne,sans-serif", fontSize:13, fontWeight:700, padding:"11px 24px", cursor:saving?"not-allowed":"pointer" }}>
              {saving?t("saving"):t("saveChanges")}
            </button>
          </div>
        </form>
      )}

      {tab==="contact"&&(
        <form onSubmit={saveContact}>
          <div style={{ background:"#ffffff", border:"1px solid #eeeeee", borderRadius:12, padding:"1.25rem" }}>
            <div style={{ fontFamily:"Syne", fontSize:14, fontWeight:700, marginBottom:"1rem", color:"#000000" }}>{t("contactDetails")}</div>
            <label style={lbl}>{t("email")}</label>
            <input style={{ ...inp, color:"#777777", cursor:"not-allowed" }} value={sensitive.email} readOnly/>
            <label style={lbl}>{t("phone")}</label>
            <input style={inp} placeholder="+254 700 000 000" value={sensitive.phone} onChange={e=>setSensitive(s=>({...s,phone:e.target.value}))}/>
            <label style={{ fontSize:12, color:"#666", marginBottom:4, display:"block", marginTop:12 }}>M-Pesa Number (for payouts)</label>
            <input style={inp} placeholder="07XX XXX XXX" value={sensitive.mpesa_number} onChange={e=>setSensitive(s=>({...s,mpesa_number:e.target.value}))}/>
            <div style={{ fontSize:11, color:"#999", marginBottom:8 }}>This number receives your service payments automatically</div>
            {/* Payment method: M-Pesa only for now - Till/Paybill removed since Working Account top-ups cannot be automated via API per Safaricom */}
            <button type="submit" disabled={saving}
              style={{ background:saving?"#555555":"#378add", border:"none", borderRadius:9, color:"#fff", fontFamily:"Syne,sans-serif", fontSize:13, fontWeight:700, padding:"11px 24px", cursor:saving?"not-allowed":"pointer" }}>
              {saving?t("saving"):t("saveChanges")}
            </button>
          </div>
        </form>
      )}

      {tab==="location"&&(
        <form onSubmit={saveLocation}>
          <div style={{ background:"#ffffff", border:"1px solid #eeeeee", borderRadius:12, padding:"1.25rem" }}>
            <div style={{ fontFamily:"Syne", fontSize:14, fontWeight:700, marginBottom:4, color:"#000000" }}>Shop location</div>
            <div style={{ fontSize:12, color:"#777777", marginBottom:"1rem" }}>Help customers find you and enable distance-based search</div>
            <button type="button" onClick={detectLocation} disabled={locating}
              style={{ background:locating?"#555555":"#f0fdf4", border:"1px solid #1d9e7540", borderRadius:9, color:"#1d9e75", fontFamily:"Syne,sans-serif", fontSize:13, fontWeight:700, padding:"10px 20px", cursor:locating?"not-allowed":"pointer", marginBottom:16, width:"100%" }}>
              {locating?"Detecting...":"📍 Use my current location"}
            </button>
            {location.address&&<div style={{ fontSize:11, color:"#777777", marginBottom:12, padding:"0.6rem", background:"#ffffff", borderRadius:7 }}>{location.address}</div>}
              <label style={lbl}>Street address</label>
              <input style={inp} placeholder="e.g. Westlands, Waiyaki Way, Nairobi" value={location.address||""} onChange={e=>setLocation(l=>({...l,address:e.target.value}))}/>
            <label style={lbl}>Or enter coordinates manually</label>
            <div style={{ display:"grid", gridTemplateColumns:isMobile?"1fr":"1fr 1fr", gap:10 }}>
              <div>
                <label style={{ ...lbl, marginBottom:4 }}>Latitude</label>
                <input style={inp} placeholder="-1.2921" value={location.latitude||""} onChange={e=>setLocation(l=>({...l,latitude:e.target.value}))}/>
              </div>
              <div>
                <label style={{ ...lbl, marginBottom:4 }}>Longitude</label>
                <input style={inp} placeholder="36.8219" value={location.longitude||""} onChange={e=>setLocation(l=>({...l,longitude:e.target.value}))}/>
              </div>
            </div>
            <button type="submit" disabled={saving}
              style={{ background:saving?"#555555":"#378add", border:"none", borderRadius:9, color:"#fff", fontFamily:"Syne,sans-serif", fontSize:13, fontWeight:700, padding:"11px 24px", cursor:saving?"not-allowed":"pointer" }}>
              {saving?"Saving...":"Save location"}
            </button>
          </div>
        </form>
      )}

      {tab==="security"&&(
        <form onSubmit={changePassword}>
          <div style={{ background:"#ffffff", border:"1px solid #eeeeee", borderRadius:12, padding:"1.25rem" }}>
            <div style={{ fontFamily:"Syne", fontSize:14, fontWeight:700, marginBottom:"1rem", color:"#000000" }}>{t("changePassword")}</div>
            <label style={lbl}>{t("newPassword")}</label>
            <input style={inp} type="password" name="password" placeholder="Min 8 characters, 1 number" required/>
            <label style={lbl}>{t("confirmPassword")}</label>
            <input style={inp} type="password" name="confirm" placeholder="Repeat password" required/>
            <button type="submit"
              style={{ background:"#378add", border:"none", borderRadius:9, color:"#fff", fontFamily:"Syne,sans-serif", fontSize:13, fontWeight:700, padding:"11px 24px", cursor:"pointer" }}>
              {t("changePassword")}
            </button>
          </div>
        </form>
      )}

      {tab==="data"&&(
        <div>
          <div style={{ background:"#ffffff", border:"1px solid #eeeeee", borderRadius:12, padding:"1.25rem", marginBottom:"1rem" }}>
            <div style={{ fontFamily:"Syne", fontSize:14, fontWeight:700, marginBottom:4, color:"#000000" }}>Your data</div>
            <div style={{ fontSize:12, color:"#666", marginBottom:"1.25rem", lineHeight:1.6 }}>
              Download all data we hold about you under the Kenya Data Protection Act 2019.
            </div>
            {exporting&&<div style={{ color:"#777777", fontSize:13, marginBottom:"1rem" }}>Loading your data...</div>}
            {exportData&&(
              <div style={{ marginBottom:"1.25rem" }}>
                <div style={{ display:"grid", gridTemplateColumns:"repeat(3,1fr)", gap:8, marginBottom:"1rem" }}>
                  {[
                    { label:"Bookings", value:exportData.bookings.length },
                    { label:"Payments", value:exportData.payments.length },
                    { label:"Reviews", value:exportData.reviews.length },
                  ].map(s=>(
                    <div key={s.label} style={{ background:"#ffffff", border:"1px solid #e5e5e5", borderRadius:8, padding:"0.75rem", textAlign:"center" }}>
                      <div style={{ fontFamily:"Syne", fontSize:18, fontWeight:800, color:"#378add" }}>{s.value}</div>
                      <div style={{ fontSize:10, color:"#777777", marginTop:2 }}>{s.label}</div>
                    </div>
                  ))}
                </div>
                <div style={{ display:"flex", gap:8, flexWrap:"wrap" }}>
                  <button onClick={()=>downloadPDF(exportData, `provider-data-${new Date().toISOString().split("T")[0]}.pdf`)}
                    style={{ background:"#f0fdf4", border:"1px solid #1d9e7540", borderRadius:8, color:"#1d9e75", fontSize:12, fontWeight:600, padding:"9px 16px", cursor:"pointer" }}>
                    <><DocumentIcon size={13} color="currentColor"/> Download PDF report</>
                  </button>
                  <button onClick={()=>downloadCSV(exportData.bookings, `bookings-${new Date().toISOString().split("T")[0]}.csv`)}
                    style={{ background:"#eff6ff", border:"1px solid #378add40", borderRadius:8, color:"#378add", fontSize:12, fontWeight:600, padding:"9px 16px", cursor:"pointer" }}>
                    <><AnalyticsIcon size={13} color="currentColor"/> Bookings CSV</>
                  </button>
                </div>
              </div>
            )}
            {!exporting&&!exportData&&(
              <button onClick={loadExportData}
                style={{ background:"#378add", border:"none", borderRadius:9, color:"#fff", fontFamily:"Syne,sans-serif", fontSize:13, fontWeight:700, padding:"11px 24px", cursor:"pointer" }}>
                Load my data
              </button>
            )}
          </div>
        </div>
      )}

      {/* Danger Zone */}
      <div style={{ background:"#fff5f5", border:"1px solid #e24b4a30", borderRadius:12, padding:"1.25rem", marginTop:"1.5rem" }}>
        <div style={{ fontFamily:"Syne", fontSize:15, fontWeight:700, color:"#e24b4a", marginBottom:6 }}>Danger Zone</div>
        <div style={{ fontSize:13, color:"#666", marginBottom:12, lineHeight:1.6 }}>
          Deleting your account permanently removes all your data within 7 days. Active bookings must be resolved first.
        </div>
        <button onClick={async()=>{
          const{data:ab}=await supabase.from("bookings").select("id").eq("provider_id",user.id).in("status",["pending","confirmed","in-progress"])
          if(ab?.length>0)return toast.error("Resolve active bookings first.")
          if(!window.confirm("Delete your account? This cannot be undone."))return
          await supabase.from("deletion_requests").insert({ user_id:user.id, requested_at:new Date().toISOString(), scheduled_for:new Date(Date.now()+7*24*60*60*1000).toISOString(), status:"pending", reason:"User requested deletion from app" })
          // Notify admin
          const { data: admins } = await supabase.from("profiles").select("id").eq("role","admin")
          if(admins?.length > 0) {
            await supabase.from("notifications").insert(admins.map(a=>({
              user_id: a.id,
              type: "account_deletion",
              title: "Account deletion request",
              message: (profile?.first_name||"A user") + " ("+user.id.slice(0,8)+") has requested account deletion. Scheduled for " + new Date(Date.now()+7*24*60*60*1000).toLocaleDateString(),
              data: { user_id: user.id }
            })))
          }
          toast.success("Deletion requested. You will be signed out.")
          // Notify admin
          supabase.from("profiles").select("id").eq("role","admin").then(({data:admins})=>{
            if(admins?.length>0) {
              supabase.from("notifications").insert(admins.map(a=>({
                user_id:a.id, type:"account_deletion",
                title:"Account deletion request ⚠️",
                message:"A "+user?.user_metadata?.role+" has requested account deletion.",
                data:{ user_id:user.id }
              })))
              // Send push to all admins
              admins.forEach(a => pushNotify.accountDeletionRequest(a.id, user?.user_metadata?.first_name||"A user", user.id))
            }
          })
          setTimeout(()=>supabase.auth.signOut(),2000)
        }} style={{ background:"#fff", border:"1px solid #e24b4a", borderRadius:8, color:"#e24b4a", fontSize:13, fontWeight:600, padding:"10px 20px", cursor:"pointer" }}>
          Request Account Deletion
        </button>
      </div>
    </div>
  )
}
